import type { apiTypes } from '@cmg/common';
import { Highlights } from '@cmg/design-system';
import type { FC } from 'react';
import { Fragment } from 'react';

import type { UserStatus } from '../../../../../types/domain/user/constants';
import { getFullName } from '../../../../../types/domain/user/constants';
import type { User } from '../../../../../types/domain/user/user';
import { UserHighlightsCreateOrEditActions } from './actions/UserHighlightsCreateOrEditActions';
import { UserHighlightsImpersonateAction } from './actions/UserHighlightsImpersonateAction';
import { UserHighlightsItems } from './UserHighlightsItems';
import { UserHighlightsTitleItems } from './UserHighlightsTitleItems';

export type Props = Readonly<{
  onReset: () => void;
  isLoading: Readonly<boolean>;
  canCreateOrEditUser: Readonly<boolean>;
  canImpersonateUser: Readonly<boolean>;
  user: Readonly<User>;
  updateUserStatusError: Readonly<apiTypes.GenericServerError | null>;
  onUpdateUserStatus: (userStatus: UserStatus) => void;
  unlockUserError: Readonly<apiTypes.GenericServerError | null>;
  onUnlockUser: () => void;
  resendInviteError: Readonly<apiTypes.GenericServerError | null>;
  onResendInviteEmail: () => void;
  targetTenantSubdomain: Readonly<string | null>;
}>;

export const UserHighlights: FC<Props> = ({
  onReset,
  canCreateOrEditUser,
  canImpersonateUser,
  user,
  isLoading,
  updateUserStatusError,
  onUpdateUserStatus,
  unlockUserError,
  onUnlockUser,
  resendInviteError,
  onResendInviteEmail,
  targetTenantSubdomain,
}) => {
  return (
    <Highlights
      title={getFullName(user)}
      titleItems={<UserHighlightsTitleItems user={user} />}
      items={<UserHighlightsItems user={user} />}
      actions={
        canCreateOrEditUser || canImpersonateUser ? (
          <Fragment>
            <UserHighlightsImpersonateAction
              onReset={onReset}
              user={user}
              isLoading={isLoading}
              canImpersonateUser={canImpersonateUser}
              targetTenantSubdomain={targetTenantSubdomain}
            />
            <UserHighlightsCreateOrEditActions
              onReset={onReset}
              user={user}
              isLoading={isLoading}
              canCreateOrEditUser={canCreateOrEditUser}
              updateUserStatusError={updateUserStatusError}
              resendInviteError={resendInviteError}
              unlockUserError={unlockUserError}
              onUnlockUser={onUnlockUser}
              onUpdateUserStatus={onUpdateUserStatus}
              onResendInviteEmail={onResendInviteEmail}
            />
          </Fragment>
        ) : undefined
      }
    />
  );
};
